import React, { useEffect } from 'react';
import { Checklist } from './OnboardingViewHelpers';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { selectModalOnboarding, selectModalPractice } from '../store/onboardingSlice';
import './portRequestModal.css';

import { selectPortRequestIds, selectCurrentlyViewedPortRequest, selectActiveTab, selectActiveTabs } from '../store/portRequestSlice';

import {
  createPortRequestTelnyx,
  updatePortRequestTelnyx,
  savePortRequest,
  sharePortRequestTelnyx,
  setPortRequestOnboarding,
  setCurrentPortRequest,
  setPortRequestType,
  setPortRequestAdminEntityName,
  setPortRequestAdminAuthPersonName,
  setPortRequestAdminBillingPhoneNumber,
  setPortRequestAdminAccountNumber,
  setPortRequestAdminPinPasscode,
  setPortRequestLocationStreetAddress,
  setPortRequestLocationExtendedAddress,
  setPortRequestLocationLocality,
  setPortRequestLocationAdministrativeArea,
  setPortRequestLocationPostalCode,
  setPortRequestLocationCountry,
  selectShareLink,
  setCreatePhoneNumbers,
  selectPortRequestType,
  selectPortRequestAdminEntityName,
  selectPortRequestAdminAuthPersonName,
  selectPortRequestAdminBillingPhoneNumber,
  selectPortRequestAdminAccountNumber,
  selectPortRequestAdminPinPasscode,
  selectPortRequestLocationStreetAddress,
  selectPortRequestLocationExtendedAddress,
  selectPortRequestLocationLocality,
  selectPortRequestLocationAdministrativeArea,
  selectPortRequestLocationPostalCode,
  selectPortRequestLocationCountry,
  selectPortRequestLoading,
  selectCreatePhoneNumbers
} from '../store/portRequestSlice';

// Function to dynamically determine field types from the actual data structure
const getFieldType = (value) => {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'object') return 'object';
  if (typeof value === 'string') return 'string';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  return 'unknown';
};

// Function to recursively build field type mapping from nested objects
const buildFieldTypeMapping = (obj, prefix = '') => {
  const mapping = {};
  
  for (const [key, value] of Object.entries(obj)) {
    const fullPath = prefix ? `${prefix}.${key}` : key;
    const fieldType = getFieldType(value);
    
    if (fieldType === 'object' && value !== null) {
      // Recursively process nested objects
      Object.assign(mapping, buildFieldTypeMapping(value, fullPath));
    } else if (fieldType === 'array' && value.length > 0) {
      // For arrays, check the first item to determine type
      const firstItemType = getFieldType(value[0]);
      if (firstItemType === 'object' && value[0] !== null) {
        // Recursively process array items if they're objects
        Object.assign(mapping, buildFieldTypeMapping(value[0], `${fullPath}[0]`));
      } else {
        mapping[fullPath] = `array<${firstItemType}>`;
      }
    } else {
      mapping[fullPath] = fieldType;
    }
  }
  
  return mapping;
};

// Example port request structure based on Telnyx API
const examplePortRequestStructure = {
  "misc": {
    "type": "select",
    "remaining_numbers_action": "text",
    "new_billing_phone_number": "text"
  },
  "end_user": {
    "admin": {
      "entity_name": "text",
      "auth_person_name": "text",
      "billing_phone_number": "number",
      "account_number": "text",
      "tax_identifier": "text",
      "pin_passcode": "number",
      "business_identifier": "text"
    },
    "location": {
      "street_address": "text",
      "extended_address": "text",
      "locality": "text",
      "administrative_area": "text",
      "postal_code": "text",
      "country_code": "select"
    }
  },
  "documents": {
    "loa": "file",
    "invoice": "file"
  },
  "activation_settings": {
    "foc_datetime_requested": "datetime-local"
  },
  "phone_number_configuration": {
    "billing_group_id": "text",
    "connection_id": "text",
    "messaging_profile_id": "text",
    "emergency_address_id": "text",
    "tags": "array"
  },
  "requirement_group_id": "text",
  "requirements": [
    {
      "field_value": "text",
      "requirement_type_id": "text"
    }
  ],
  "user_feedback": {
    "user_rating": "number",
    "user_comment": "text"
  },
  "webhook_url": "text",
  "customer_reference": "text",
  "messaging": {
    "enable_messaging": "checkbox"
  }
};


// Generate the field type mapping dynamically from the example structure
const portRequestFormFieldTypes = buildFieldTypeMapping(examplePortRequestStructure);

// Function to update field types from actual port request data
const updateFieldTypesFromData = (portRequestData) => {
  if (!portRequestData) return portRequestFormFieldTypes;
  return buildFieldTypeMapping(portRequestData);
};

// Debug: Log the generated field type mapping
console.log('Generated portRequestFormFieldTypes:', portRequestFormFieldTypes);

const PortRequestModal = () => {
   
  const dispatch = useAppDispatch();
  const modalOnboarding = useAppSelector(state => selectModalOnboarding(state));
  const modalPractice = useAppSelector(state => selectModalPractice(state));

  const portRequestIds = useAppSelector(state => selectPortRequestIds(state));
  const currentlyViewedPortRequest = useAppSelector(state => selectCurrentlyViewedPortRequest(state));


  const activeTab = useAppSelector(state => selectActiveTab(state));
  const activeTabs = useAppSelector(state => selectActiveTabs(state));
  const loading = useAppSelector(state => selectPortRequestLoading(state));

  useEffect(() => {
    dispatch(setPortRequestOnboarding({ onboarding: modalOnboarding, practice: modalPractice }));
  }, [dispatch, modalOnboarding, modalPractice, loading]);

  // Update field types when port request data changes
  useEffect(() => {
    if (currentlyViewedPortRequest) {
      const dynamicFieldTypes = updateFieldTypesFromData(currentlyViewedPortRequest);
      console.log('Dynamic field types from actual data:', dynamicFieldTypes);
    }
  }, [currentlyViewedPortRequest]);


  console.log("PortRequestModal: ", modalOnboarding, modalPractice);
  console.log("portRequestIds: ", portRequestIds);
  console.log("currentlyViewedPortRequest: ", currentlyViewedPortRequest);

  // Sample checklist data - in a real app, this would come from props or API
  const stageCheckList = {
    "onDemandStatus": {
      "workflows": ["voip", "non-voip", "dso"],
      "description": "Make sure on-demand status is communicated with client"
    },
    "voiceAPI": {
      "workflows": ["voip", "non-voip", "dso"],
      "description": "Make sure voice API is configured to run automatically"
    },
    "voipConfig": {
      "workflows": ["voip", "non-voip", "dso"],
      "description": "Make sure to set voice API for fax correctly afterwards if running a joint port"
    }
  };

  const handleSelectPortRequest = (tabValue) => {
    dispatch(setCurrentPortRequest({ activeTab: tabValue }));
  };


  const RenderCreateForm = () => {
    console.log("RenderCreateForm");
    const phoneNumbers = useAppSelector(selectCreatePhoneNumbers);
    return (
      <div className="port-request-create-form space-y-4">
        <div className="port-request-header">
          <h3 className="port-request-form-title">Create New Port Request</h3>
          <p className="port-request-form-subtitle">Fill out the form below to create a new port request</p>
        </div>
        
        <div className="port-request-actions">
          <button
            onClick={() => {
              dispatch(createPortRequestTelnyx({ onboardingId: modalOnboarding.id, phoneNumbers: phoneNumbers }));
              console.log("Create button clicked");
            }}
            className="port-request-button port-request-button-primary"
          >
            Create Port Request
          </button>
          {phoneNumbers.map((phoneNumber, index) => (
            <input type="text" value={phoneNumber} onChange={(e) => dispatch(setCreatePhoneNumbers({index: index, value: e.target.value}))} key={index} />
          ))}
          <button onClick={() => dispatch(setCreatePhoneNumbers({index: phoneNumbers.length, value: ''}))}>Add Phone Number</button>
        </div>
      </div>
    );
  };

  const getIndentStyle = (key_path) => {
    return { marginLeft: `${key_path.length * 20}px` };
  }


  const RenderFormText = ({ key_path, dispatcher, selectFormValue}) => {
    const nested_val = useAppSelector(selectFormValue);
    return (
      <div>
        <button onClick={() => dispatch(savePortRequest({ onboardingId: modalOnboarding.id, portRequestId: currentlyViewedPortRequest.id, saveKey: key_path, saveValue: nested_val }))}>Save</button>
      <div style={getIndentStyle(key_path)} className="json-field-primitive json-field-string mb-2">
        <div className="json-field-input-container">
          <span className="json-field-key">{key_path[key_path.length - 1]}:</span>
          <input type="text" value={nested_val || ''} onChange={(e) => dispatch(dispatcher(e.target.value))} disabled={dispatcher === null} className="json-field-input json-field-input-yellow" />
          <span className="json-syntax">"</span>
        </div>
      </div>
      </div>
    );
  }

  const RenderUpdateTab = () => {
    const shareLink = useAppSelector(selectShareLink);
    return (
      <div>
        <button onClick={() => dispatch(updatePortRequestTelnyx({ onboardingId: modalOnboarding.id, portRequestId: currentlyViewedPortRequest.id }))}>Update Port Request</button>
        <button onClick={() => dispatch(sharePortRequestTelnyx({ onboardingId: modalOnboarding.id, portRequestId: currentlyViewedPortRequest.id }))}>Share Link</button>
        <div>
          {shareLink && <div>Share Link: {shareLink}</div>}
        </div>
        <div className="port-request-form-container">
          <RenderFormText key_path={['misc', 'type']} dispatcher={setPortRequestType} selectFormValue={selectPortRequestType} />
          {/* <RenderFormText key_path={['misc', 'remaining_numbers_action']} dispatcher={setPortRequestRemainingNumbersAction} selectFormValue={selectPortRequestRemainingNumbersAction} /> */}
          {/* <RenderFormText key_path={['misc', 'new_billing_phone_number']} dispatcher={setPortRequestNewBillingPhoneNumber} selectFormValue={selectPortRequestNewBillingPhoneNumber} /> */}

          <RenderFormText key_path={['end_user', 'admin','entity_name']} dispatcher={setPortRequestAdminEntityName} selectFormValue={selectPortRequestAdminEntityName} />
          <RenderFormText key_path={['end_user', 'admin','auth_person_name']} dispatcher={setPortRequestAdminAuthPersonName} selectFormValue={selectPortRequestAdminAuthPersonName} />
          <RenderFormText key_path={['end_user', 'admin','billing_phone_number']} dispatcher={setPortRequestAdminBillingPhoneNumber} selectFormValue={selectPortRequestAdminBillingPhoneNumber} />
          <RenderFormText key_path={['end_user', 'admin','account_number']} dispatcher={setPortRequestAdminAccountNumber} selectFormValue={selectPortRequestAdminAccountNumber} />
          {/* <RenderFormText key_path={['end_user', 'admin','tax_identifier']} dispatcher={setPortRequestAdminTaxIdentifier} selectFormValue={selectPortRequestAdminTaxIdentifier} /> */}
          <RenderFormText key_path={['end_user', 'admin','pin_passcode']} dispatcher={setPortRequestAdminPinPasscode} selectFormValue={selectPortRequestAdminPinPasscode} />
          {/* <RenderFormText key_path={['end_user', 'admin','business_identifier']} dispatcher={setPortRequestAdminBusinessIdentifier} selectFormValue={selectPortRequestAdminBusinessIdentifier} /> */}

          <RenderFormText key_path={['end_user', 'location','street_address']} dispatcher={setPortRequestLocationStreetAddress} selectFormValue={selectPortRequestLocationStreetAddress} />
          <RenderFormText key_path={['end_user', 'location','extended_address']} dispatcher={setPortRequestLocationExtendedAddress} selectFormValue={selectPortRequestLocationExtendedAddress} />
          <RenderFormText key_path={['end_user', 'location','locality']} dispatcher={setPortRequestLocationLocality} selectFormValue={selectPortRequestLocationLocality} />
          <RenderFormText key_path={['end_user', 'location','administrative_area']} dispatcher={setPortRequestLocationAdministrativeArea} selectFormValue={selectPortRequestLocationAdministrativeArea} />
          <RenderFormText key_path={['end_user', 'location','postal_code']} dispatcher={setPortRequestLocationPostalCode} selectFormValue={selectPortRequestLocationPostalCode} />
          <RenderFormText key_path={['end_user', 'location','country']} dispatcher={setPortRequestLocationCountry} selectFormValue={selectPortRequestLocationCountry} />

          {/* <RenderFormFile key_path={['documents', 'loa']} dispatcher={setPortRequestDocumentsLoa} selectFormValue={selectPortRequestDocumentsLoa} /> */}
          {/* <RenderFormFile key_path={['documents', 'invoice']} dispatcher={setPortRequestDocumentsInvoice} selectFormValue={selectPortRequestDocumentsInvoice} /> */}

          {/* <RenderFormDatetimeLocal key_path={['activation_settings', 'foc_datetime_requested']} dispatcher={setPortRequestActivationSettingsFocDatetimeRequested} selectFormValue={selectPortRequestActivationSettingsFocDatetimeRequested} /> */}

          {/* <RenderFormText key_path={['phone_number_configuration', 'billing_group_id']} dispatcher={setPortRequestPhoneNumberConfigurationBillingGroupId} selectFormValue={selectPortRequestPhoneNumberConfigurationBillingGroupId} /> */}
          {/* <RenderFormText key_path={['phone_number_configuration', 'connection_id']} dispatcher={setPortRequestPhoneNumberConfigurationConnectionId} selectFormValue={selectPortRequestPhoneNumberConfigurationConnectionId} /> */}
          {/* <RenderFormText key_path={['phone_number_configuration', 'messaging_profile_id']} dispatcher={setPortRequestPhoneNumberConfigurationMessagingProfileId} selectFormValue={selectPortRequestPhoneNumberConfigurationMessagingProfileId} /> */}
          {/* <RenderFormText key_path={['phone_number_configuration', 'emergency_address_id']} dispatcher={setPortRequestPhoneNumberConfigurationEmergencyAddressId} selectFormValue={selectPortRequestPhoneNumberConfigurationEmergencyAddressId} /> */}
          {/* <RenderFormText key_path={['phone_number_configuration', 'tags']} dispatcher={setPortRequestPhoneNumberConfigurationTags} selectFormValue={selectPortRequestPhoneNumberConfigurationTags} /> */}


          {/* <RenderFormText key_path={['requirement_group_id']} dispatcher={setPortRequestRequirementGroupId} selectFormValue={selectPortRequestRequirementGroupId} /> */}

        </div>
      </div>
    )
  }

  const RenderTabContent = () => {

    if (activeTab === 'create') {
      console.log("renderCreateForm ");
      return <div><RenderCreateForm /></div>;
    }

    console.log("renderTabContent ");
    if (!currentlyViewedPortRequest) {
      console.log("RenderTabContent: currentRequest not found");
      return <div className="port-request-status-message">Port request not found or Loading...</div>;
    }

    if (!currentlyViewedPortRequest?.status) {
      console.log("RenderTabContent: currentlyViewedPortRequest.status not found");
      return <div className="port-request-status-message">Port request status not found</div>;
    }

    switch (currentlyViewedPortRequest?.status?.value) {
      case 'draft':
      case 'updated':
        return <div><RenderUpdateTab /></div>;
      // case 'review':
      //   return renderReviewTab(currentRequest);
      // case 'pending':
      //   return renderPendingTab(currentRequest);
      // case 'completed':
      //   return renderCompletedTab(currentRequest);
      default:
        return <div className="port-request-status-message">Unknown status: {currentlyViewedPortRequest.status.value}</div>;
    }
  };

  return (
    <div className="port-request-modal space-y-4">
     

      {/* Show tabs for existing port requests */}
      {activeTabs && activeTabs.length > 0 && (
        <div className="port-request-tabs">
          <nav className="port-request-nav nav">
            {activeTabs.map((tabValue) => (
              <button
                key={tabValue}
                onClick={() => handleSelectPortRequest(tabValue)}
                className={`port-request-tab py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tabValue
                    ? 'port-request-tab active border-blue-500 text-blue-600'
                    : 'port-request-tab hover border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tabValue}
              </button>
            ))}
          </nav>
        </div>
      )}

      <div className="port-request-content mt-4">
        <RenderTabContent /> 
      </div>
      
      {/* Checklist Component */}
      <Checklist 
        stage="portRequest" 
        onboarding={modalOnboarding} 
        stateCheckList={stageCheckList} 
      />
    </div>
  );
};

export const displayHistoryPortRequest = (onboarding) => {
  // For now, return a simple table row with placeholder data
  // In the future, this would parse actual history data from the onboarding object
  return (
    <tr className="port-request-table-row">
      <td className="port-request-table-cell">
        {new Date().toLocaleString()}
      </td>
      <td className="port-request-table-cell">
        Not set
      </td>
      <td className="port-request-table-cell">
        {onboarding.portRequest ? '✅' : '❌'}
      </td>
      <td className="port-request-table-cell">
        Initial status
      </td>
    </tr>
  );
};

export { PortRequestModal };
